/**
 * Composer — chat message input shell.
 *
 * Phase 1b: replaces the visual shell of ChatInput.tsx. All existing hook logic
 * (useSlashCommands, history navigation, entity chips, task/client forms) is
 * brought in 1:1 from the original ChatInput — no behavioural rewrites.
 *
 * ChatInput.tsx is reduced to a ≤10-line re-export shim for back-compat.
 *
 * Tab order (Phase 1b §4.2 I5 contract):
 *   textarea → send button (tab-focusable even when aria-disabled) → help button (tabIndex=-1)
 * This ensures Tab from a scroll button above Composer lands on the textarea first.
 */

import { useState, useRef, useCallback, useEffect, type KeyboardEvent, type ChangeEvent, type RefObject } from "react";
import type { EntityChip, SlashCommandResult, ClientUserFormData } from "../../types/index.js";
import { useSlashCommands } from "../../hooks/useSlashCommands.js";
import SlashCommandDropdown from "../SlashCommandDropdown.js";
import ClientUserForm from "../ClientUserForm.js";
import TaskCreateForm from "../TaskCreateForm.js";
import { apiPost } from "../../lib/api.js";
import NudgeStrip from "./NudgeStrip.js";

export interface ComposerProps {
	onSend: (message: string, entityRefs?: EntityChip[]) => void;
	disabled?: boolean;
	/** External ref to the textarea so the parent can focus it (e.g. after streaming ends). */
	inputRef?: RefObject<HTMLTextAreaElement>;
	/** Currently selected project ID — needed for slash command entity search. */
	projectId?: string | null;
	/** Base API URL — passed through to TaskCreateForm for task list fetching. */
	apiUrl?: string;
	/**
	 * Optional command handler. When present, handleSend checks whether the
	 * trimmed input is a slash command before passing it to onSend. If
	 * onCommand returns true, the command is consumed and onSend is not called.
	 */
	onCommand?: (command: string) => boolean;
}

/** Maximum number of sent messages to hold in session history. */
const HISTORY_LIMIT = 50;

/**
 * Chat message input with send button.
 *
 * Supports:
 * - Enter to send (Shift+Enter for new line)
 * - Auto-resizing textarea
 * - Up/Down arrow to cycle through previous messages sent this session
 * - External focus control via inputRef prop
 * - Slash commands: /u for users, /t for tasks (also opens TaskCreateForm), /m for milestones, /s for statuses
 * - A command hint panel that appears on bare `/` before a prefix is typed
 * - A help icon button that toggles the command hint panel explicitly
 * - Entity chips displayed inline before the textarea
 * - TaskCreateForm modal for structured task creation via /t
 * - NudgeStrip structural placeholder (hidden in Phase 1b)
 */
export default function Composer({ onSend, disabled = false, inputRef, projectId = null, apiUrl = "", onCommand }: ComposerProps) {
	const [text, setText] = useState("");
	const internalRef = useRef<HTMLTextAreaElement>(null);

	// Use external ref if provided, otherwise fall back to internal ref
	const resolvedRef: RefObject<HTMLTextAreaElement> = inputRef ?? internalRef;

	// Session-scoped message history (not persisted across page refreshes)
	const historyRef = useRef<string[]>([]);
	// Index into history: -1 means "not browsing history" (current draft)
	const historyIndexRef = useRef(-1);
	// Preserve the user's in-progress draft while browsing history
	const draftRef = useRef("");

	const {
		results,
		isOpen: slashOpen,
		showCommandHint,
		activeIndex,
		selectedChips,
		isLoading: slashLoading,
		activeType,
		showClientForm,
		showTaskForm,
		processInput,
		handleKeyDown: slashHandleKeyDown,
		selectResult,
		removeChip,
		resetSlashState,
		closeClientForm,
		closeTaskForm,
	} = useSlashCommands(projectId);

	// Whether to show the command hint panel (can also be toggled by the help icon)
	const [helpPanelOpen, setHelpPanelOpen] = useState(false);

	// Focus the textarea on mount
	useEffect(() => {
		resolvedRef.current?.focus();
	}, [resolvedRef]);

	const resetHeight = useCallback(() => {
		if (resolvedRef.current) {
			resolvedRef.current.style.height = "auto";
		}
	}, [resolvedRef]);

	// Client user form state
	const [clientFormStatus, setClientFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
	const [clientFormError, setClientFormError] = useState<string | undefined>();

	const handleClientFormSubmit = useCallback(async (data: ClientUserFormData) => {
		setClientFormStatus("loading");
		setClientFormError(undefined);
		try {
			// Map frontend form field names to the backend contract
			// (POST /api/contacts/:zohoProjectId expects snake_case keys).
			const payload = {
				name: data.name,
				email: data.email,
				company: data.companyName,
				role: data.projectRole,
			};
			const response = await apiPost(`/api/contacts/${projectId}`, payload);
			if (response.ok) {
				setClientFormStatus("success");
				// Auto-close after a brief success display
				setTimeout(() => {
					closeClientForm();
					setClientFormStatus("idle");
					setClientFormError(undefined);
					// Clear the /c text from the input
					if (resolvedRef.current) {
						resolvedRef.current.value = "";
					}
					setText("");
					resetSlashState();
				}, 1500);
			} else {
				const body = await response.json().catch(() => ({})) as Record<string, unknown>;
				const msg = typeof body.message === "string" ? body.message :
					typeof body.error === "string" ? body.error : "Failed to add client user.";
				setClientFormStatus("error");
				setClientFormError(msg);
			}
		} catch {
			setClientFormStatus("error");
			setClientFormError("Network error. Please try again.");
		}
	}, [projectId, closeClientForm, resolvedRef, resetSlashState]);

	const handleClientFormCancel = useCallback(() => {
		closeClientForm();
		setClientFormStatus("idle");
		setClientFormError(undefined);
		// Clear the /c text from the input
		if (resolvedRef.current) {
			resolvedRef.current.value = "";
		}
		setText("");
	}, [closeClientForm, resolvedRef]);

	/** Handler for task form submission — sends assembled message through normal pipeline. */
	const handleTaskFormSubmit = useCallback((message: string) => {
		// Clear the /t text from the input
		if (resolvedRef.current) {
			resolvedRef.current.value = "";
		}
		setText("");
		resetSlashState();
		// Send the assembled message through the normal pipeline
		onSend(message);
	}, [onSend, resolvedRef, resetSlashState]);

	const handleTaskFormClose = useCallback(() => {
		closeTaskForm();
		// Clear the /t text from the input
		if (resolvedRef.current) {
			resolvedRef.current.value = "";
		}
		setText("");
	}, [closeTaskForm, resolvedRef]);

	const handleSend = useCallback(() => {
		const trimmed = text.trim();
		if (!trimmed || disabled) return;

		// Check if this is a slash command that the parent wants to handle
		if (onCommand && trimmed.startsWith("/") && onCommand(trimmed)) {
			setText("");
			resetHeight();
			resetSlashState();
			requestAnimationFrame(() => {
				resolvedRef.current?.focus();
			});
			return;
		}

		// Add to session history, keeping the most recent HISTORY_LIMIT items
		historyRef.current = [trimmed, ...historyRef.current].slice(0, HISTORY_LIMIT);
		// Reset history browsing state
		historyIndexRef.current = -1;
		draftRef.current = "";

		// Pass entity chips alongside the message text (always an array, never undefined)
		const chips = selectedChips.length > 0 ? [...selectedChips] : [];
		onSend(trimmed, chips);
		setText("");
		resetHeight();
		resetSlashState();

		// Re-focus after send so the user can immediately type their next message
		requestAnimationFrame(() => {
			resolvedRef.current?.focus();
		});
	}, [text, disabled, onSend, onCommand, resetHeight, resolvedRef, selectedChips, resetSlashState]);

	/**
	 * Handle selecting a result from the slash command dropdown.
	 * Updates the input text by replacing the slash command with a reference token.
	 */
	const handleSlashSelect = useCallback(
		(result: SlashCommandResult) => {
			const newText = selectResult(result, text);
			setText(newText);

			// Re-focus and move cursor to end
			requestAnimationFrame(() => {
				const el = resolvedRef.current;
				if (el) {
					el.focus();
					el.selectionStart = newText.length;
					el.selectionEnd = newText.length;
					// Recalc height
					el.style.height = "auto";
					el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
				}
			});
		},
		[selectResult, text, resolvedRef],
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			// Close the hint panel on Escape without clearing the input
			if (e.key === "Escape" && (showCommandHint || helpPanelOpen)) {
				e.preventDefault();
				setHelpPanelOpen(false);
				resetSlashState();
				return;
			}

			// Let slash command system handle keys first when dropdown is open
			if (slashOpen) {
				const consumed = slashHandleKeyDown(e);
				if (consumed) {
					// If Enter was pressed and we have a result to select, do it
					if (e.key === "Enter" && results.length > 0 && activeIndex >= 0 && activeIndex < results.length) {
						handleSlashSelect(results[activeIndex]);
					}
					return;
				}
			}

			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSend();
				return;
			}

			// Up arrow: navigate backwards through history
			if (e.key === "ArrowUp") {
				const history = historyRef.current;
				if (history.length === 0) return;

				// Only activate when input is empty or already browsing history
				if (historyIndexRef.current === -1 && text.trim() !== "") return;

				e.preventDefault();

				if (historyIndexRef.current === -1) {
					// Save the current draft before navigating away
					draftRef.current = text;
				}

				const nextIndex = Math.min(historyIndexRef.current + 1, history.length - 1);
				historyIndexRef.current = nextIndex;
				const recalled = history[nextIndex];
				setText(recalled);

				// Move caret to end after React updates the value
				requestAnimationFrame(() => {
					const el = resolvedRef.current;
					if (el) {
						el.selectionStart = recalled.length;
						el.selectionEnd = recalled.length;
						el.style.height = "auto";
						el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
					}
				});
				return;
			}

			// Down arrow: navigate forwards (towards more recent messages / back to draft)
			if (e.key === "ArrowDown") {
				if (historyIndexRef.current === -1) return;

				e.preventDefault();

				const prevIndex = historyIndexRef.current - 1;
				if (prevIndex < 0) {
					// Reached the end of history — restore the saved draft
					historyIndexRef.current = -1;
					const draft = draftRef.current;
					setText(draft);
					requestAnimationFrame(() => {
						const el = resolvedRef.current;
						if (el) {
							el.selectionStart = draft.length;
							el.selectionEnd = draft.length;
							el.style.height = "auto";
							el.style.height = draft
								? `${Math.min(el.scrollHeight, 160)}px`
								: "";
						}
					});
				} else {
					historyIndexRef.current = prevIndex;
					const recalled = historyRef.current[prevIndex];
					setText(recalled);
					requestAnimationFrame(() => {
						const el = resolvedRef.current;
						if (el) {
							el.selectionStart = recalled.length;
							el.selectionEnd = recalled.length;
							el.style.height = "auto";
							el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
						}
					});
				}
			}
		},
		[handleSend, text, resolvedRef, slashOpen, slashHandleKeyDown, results, activeIndex, handleSlashSelect, showCommandHint, helpPanelOpen, resetSlashState],
	);

	const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		// Any manual edit exits history-browsing mode
		historyIndexRef.current = -1;
		draftRef.current = "";
		const value = e.target.value;
		setText(value);

		// Process for slash commands
		const cursorPos = e.target.selectionStart ?? value.length;
		processInput(value, cursorPos);

		// Auto-resize textarea
		const textarea = e.target;
		textarea.style.height = "auto";
		textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;

		// Close the manually toggled help panel when typing resumes
		if (helpPanelOpen) {
			setHelpPanelOpen(false);
		}
	}, [processInput, helpPanelOpen]);

	/** Whether the hint panel is visible from any source. */
	const hintVisible = showCommandHint || helpPanelOpen;

	return (
		<div className="chat-input-container">
			{/* Entity chips rendered above the input when present */}
			{selectedChips.length > 0 && (
				<div className="entity-chips" aria-label="Selected entities">
					{selectedChips.map((chip) => (
						<span
							key={`${chip.type}-${chip.id}`}
							className={`entity-chip entity-chip--${chip.type}`}
						>
							<span className="entity-chip__icon" aria-hidden="true">
								{chip.type === "user" ? (
									<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
										<circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<path d="M2.5 14c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
									</svg>
								) : chip.type === "milestone" ? (
									<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
										<line x1="4" y1="2" x2="4" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
										<path d="M4 3h8l-2 2.5 2 2.5H4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
									</svg>
								) : chip.type === "status" ? (
									<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
										<circle cx="8" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<circle cx="8" cy="8" r="2" fill="currentColor" />
									</svg>
								) : (
									<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
										<rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
									</svg>
								)}
							</span>
							<span className="entity-chip__label">{chip.label}</span>
							<button
								type="button"
								className="entity-chip__remove"
								onClick={() => removeChip(chip.id)}
								aria-label={`Remove ${chip.label}`}
							>
								<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
									<path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
								</svg>
							</button>
						</span>
					))}
				</div>
			)}

			<div className="chat-input-wrapper">
				{/* Slash command results dropdown — shown after prefix + query */}
				{slashOpen && (
					<SlashCommandDropdown
						results={results}
						activeIndex={activeIndex}
						isLoading={slashLoading}
						activeType={activeType}
						onSelect={handleSlashSelect}
					/>
				)}

				{/* Slash command hint — shown on bare `/` or when help icon is clicked */}
				{hintVisible && !slashOpen && (
					<div className="slash-hint" role="status" aria-label="Available slash commands" aria-live="polite">
						<div className="slash-hint-header">Available commands</div>
						<ul className="slash-hint-list">
							<li className="slash-hint-item">
								<span className="slash-hint-item-icon" aria-hidden="true">
									<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
										<circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<path d="M2.5 14c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
									</svg>
								</span>
								<kbd className="slash-hint-kbd">/u</kbd>
								<span className="slash-hint-desc">Search team members<em className="slash-hint-example">e.g. /u Sarah — mention a team member</em></span>
							</li>
							<li className="slash-hint-item">
								<span className="slash-hint-item-icon" aria-hidden="true">
									<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
										<rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
									</svg>
								</span>
								<kbd className="slash-hint-kbd">/t</kbd>
								<span className="slash-hint-desc">Create a task<em className="slash-hint-example">e.g. /t — open task creation form</em></span>
							</li>
							<li className="slash-hint-item">
								<span className="slash-hint-item-icon" aria-hidden="true">
									<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
										<line x1="4" y1="2" x2="4" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
										<path d="M4 3h8l-2 2.5 2 2.5H4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
									</svg>
								</span>
								<kbd className="slash-hint-kbd">/m</kbd>
								<span className="slash-hint-desc">Search milestones<em className="slash-hint-example">e.g. /m Q2 Release — reference a milestone</em></span>
							</li>
							<li className="slash-hint-item">
								<span className="slash-hint-item-icon" aria-hidden="true">
									<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
										<circle cx="8" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<circle cx="8" cy="8" r="2" fill="currentColor" />
									</svg>
								</span>
								<kbd className="slash-hint-kbd">/s</kbd>
								<span className="slash-hint-desc">Search task statuses<em className="slash-hint-example">e.g. /s In Progress — filter by status</em></span>
							</li>
							<li className="slash-hint-item">
								<span className="slash-hint-item-icon" aria-hidden="true">
									<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
										<circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<path d="M1.5 14c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
										<line x1="13" y1="6" x2="13" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
										<line x1="11" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
									</svg>
								</span>
								<kbd className="slash-hint-kbd">/c</kbd>
								<span className="slash-hint-desc">Add client user<em className="slash-hint-example">e.g. /c — add a client contact</em></span>
							</li>
							<li className="slash-hint-item">
								<span className="slash-hint-item-icon" aria-hidden="true">
									<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
										<rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
										<rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
									</svg>
								</span>
								<kbd className="slash-hint-kbd">/dashboard</kbd>
								<span className="slash-hint-desc">Project dashboard<em className="slash-hint-example">e.g. /dashboard — full-screen project overview</em></span>
							</li>
						</ul>
						<div className="slash-hint-footer">
							Type a space after the command to start searching
						</div>
					</div>
				)}

				{/* Textarea — first focusable element in Composer (I5 contract).
				    Positioned before send button in DOM so Tab from a scroll button
				    above lands here directly. */}
				<textarea
					ref={resolvedRef}
					className="chat-input"
					value={text}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					placeholder="Ask about your project…"
					disabled={disabled}
					rows={1}
					aria-label="Message input"
					aria-expanded={slashOpen}
					aria-haspopup="listbox"
					aria-controls={slashOpen ? "slash-command-listbox" : undefined}
				/>

				{/* Send button — uses aria-disabled rather than disabled so it retains
				    tab focus even when logically inactive, satisfying the I5 tab-order
				    contract (textarea → send button). Clicks are guarded by handleSend. */}
				<button
					className="chat-send-btn"
					onClick={handleSend}
					aria-disabled={disabled || !text.trim()}
					type="button"
					aria-label="Send message"
				>
					Send
				</button>

				{/* Help icon button — after send button in DOM so tab order is:
				    textarea → send → (help is tabIndex=-1, accessed via click/shortcut).
				    Phase 1b §4.2 I5: help is excluded from natural tab sequence. */}
				<button
					type="button"
					className={`chat-help-btn${helpPanelOpen ? " chat-help-btn--active" : ""}`}
					onClick={() => {
						setHelpPanelOpen((o) => !o);
					}}
					aria-label="Show available slash commands"
					aria-expanded={hintVisible}
					title="Available commands"
					tabIndex={-1}
				>
					<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
						<text x="2" y="13" fontFamily="monospace" fontSize="13" fontWeight="700" fill="currentColor">/</text>
					</svg>
				</button>
			</div>

			{/* NudgeStrip — structural placeholder, hidden in Phase 1b */}
			<NudgeStrip hidden={true} />

			{showClientForm && projectId && (
				<ClientUserForm
					projectId={projectId}
					onSubmit={handleClientFormSubmit}
					onCancel={handleClientFormCancel}
					status={clientFormStatus}
					errorMessage={clientFormError}
				/>
			)}

			{showTaskForm && projectId && (
				<TaskCreateForm
					projectId={projectId}
					apiUrl={apiUrl}
					onSubmit={handleTaskFormSubmit}
					onClose={handleTaskFormClose}
				/>
			)}
		</div>
	);
}
